import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { AlertTriangle, MapPin, Clock, Loader2, ExternalLink, Map as MapIcon, Eye, Download } from 'lucide-react';
import FilterBar from './FilterBar';
import VisitDetailsModal from './VisitDetailsModal'; // eslint-disable-line no-unused-vars
import StaffDateGate, { gateScopeParams } from './StaffDateGate';
import { isGoogleEnabled, trackGoogle } from '../../lib/googleApiTrack';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Unplan Visits Report (v2.5.0 — approval workflow retired).
 *
 * Auto-detected geofence exits during work hours. Now a read-only
 * report — Debit / Waive workflow has been removed; the org processes
 * these manually against reports.
 *
 * Guarantees:
 *   • Defaults to TODAY only. No fetch on mount.
 *   • Requires staff + date range pick before Load.
 *   • Keeps export + route-map viewer for investigation.
 */
export default function NonPlanVisits() {
  const today = new Date().toISOString().slice(0, 10);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [gate, setGate] = useState({
    geo_fence_id: null, geo_fence_name: null, staff: null,
    from: today, to: today,
  });
  const [routeOpen, setRouteOpen] = useState(null);      // { visit, gpsPath, loading }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visits.filter(v => {
      if (dateRange.from || dateRange.to) {
        const t = v.start?.time;
        if (!t) return false;
        const day = new Date(t).toISOString().slice(0, 10);
        if (dateRange.from && day < dateRange.from) return false;
        if (dateRange.to && day > dateRange.to) return false;
      }
      if (q && !(v.staff_name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [visits, search, dateRange]);

  const fetchVisits = useCallback(async () => {
    const scope = gateScopeParams(gate);
    if (!scope) return;
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(scope);
      const r = await fetch(`${API_URL}/api/admin/non-plan-visits?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = r.ok ? await r.json() : [];
      setVisits(data);
    } finally {
      setLoading(false);
    }
  }, [gate.geo_fence_id, gate.staff?.id, gate.from, gate.to]);

  const applyGate = async () => {
    setLoading(true); setHasLoaded(true);
    await fetchVisits();
  };

  const openRoute = async (v) => {
    setRouteOpen({ visit: v, gpsPath: [], loading: true });
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/non-plan-visits/${v.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const d = r.ok ? await r.json() : { gps_path: [] };
      setRouteOpen({ visit: d, gpsPath: d.gps_path || [], loading: false });
    } catch {
      setRouteOpen({ visit: v, gpsPath: [], loading: false });
    }
  };

  const fmtTime = (t) => t ? new Date(t).toLocaleString('en-IN', { hour12: true, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDur = (v) => {
    const h = v.duration_hours;
    const m = v.duration_minutes;
    if (h && h > 0) return `${h.toFixed(2)} hr`;
    if (m && m > 0) return `${Math.round(m)} min`;
    if (!v.end) return 'still outside';
    return '—';
  };

  // Export the current filter scope to Excel via the backend endpoint.
  // Honours the gate (staff / geo / all-global / dates) AND the on-screen
  // status + search filters by slicing the response before download when
  // a search/status filter is active. (June 18, 2026 user ask.)
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    const scope = gateScopeParams(gate);
    if (!scope) { alert('Please load a date / staff scope first.'); return; }
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(scope);
      const r = await fetch(`${API_URL}/api/admin/non-plan-visits/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `unplan_visits_${gate.from || 'all'}_${gate.to || 'today'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Export failed: ${e.message || e}`);
    } finally { setExporting(false); }
  };

  if (loading && !hasLoaded) return <div className="text-center py-8">Loading...</div>;

  return (
    <div data-testid="nonplan-visits">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Unplan Visits {hasLoaded && `(${visits.length})`}</h1>
      <p className="text-xs text-gray-500 mb-4">
        Read-only report of auto-detected geofence exits during work hours. Debit / Waive workflow retired — reviewed manually against reports.
      </p>

      <div className="mb-4">
        <StaffDateGate
          value={gate}
          onChange={setGate}
          onApply={applyGate}
          loading={loading}
          defaultDays={1}
          extras={
            hasLoaded ? (
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded-lg"
                title="Download all unplan visits in the current scope as Excel"
                data-testid="nonplan-export-btn"
              >
                {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                {exporting ? 'Exporting…' : 'Export Excel'}
              </button>
            ) : null
          }
        />
      </div>

      {!hasLoaded && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center text-blue-700" data-testid="nonplan-prompt">
          <Eye className="mx-auto mb-2 opacity-60" size={28} />
          <p className="font-medium">Pick a staff member and date range, then tap <strong>Load</strong>.</p>
          <p className="text-xs text-blue-600 mt-1">Loads today by default — widen the window to see more.</p>
        </div>
      )}

      {hasLoaded && (
      <FilterBar
        testId="nonplan-filters"
        placeholder="Search by staff name…"
        search={search}
        onSearch={setSearch}
        dateRange={dateRange}
        onDateChange={setDateRange}
        onClear={() => { setSearch(''); setDateRange({ from: '', to: '' }); }}
        resultCount={filtered.length}
      />
      )}

      {hasLoaded && filtered.length === 0 && (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500" data-testid="nonplan-empty">
          {visits.length === 0 ? 'No unplanned exits in this window.' : 'No records match the current filter.'}
        </div>
      )}

      {hasLoaded && (
      <div className="space-y-3">
        {filtered.map(v => {
          return (
            <div key={v.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-testid={`nonplan-${v.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-2 min-w-0">
                  <AlertTriangle className="text-orange-500 shrink-0 mt-0.5" size={18} />
                  <div className="min-w-0">
                    <h3 className="font-semibold truncate">{v.staff_name || v.staff_id}</h3>
                    <p className="text-xs text-gray-500">
                      {v.trigger === 'gps_silent_gap' ? 'Silent GPS gap' : 'Left geofence without plan'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">When left</p>
                  <p className="font-medium">{fmtTime(v.start?.time)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">When returned</p>
                  <p className="font-medium">{v.end ? fmtTime(v.end.time) : <span className="text-orange-600">still outside</span>}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase flex items-center gap-1"><Clock size={11} /> Duration</p>
                  <p className="font-bold text-orange-700">{fmtDur(v)}</p>
                </div>
                <div className="flex items-end gap-2">
                  <a
                    href={`https://maps.google.com/?q=${v.start?.latitude},${v.start?.longitude}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                    data-testid={`nonplan-exit-pin-${v.id}`}
                  >
                    <MapPin size={12} /> Exit pin <ExternalLink size={10} />
                  </a>
                  <button
                    onClick={() => openRoute(v)}
                    className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-md hover:bg-blue-100"
                    data-testid={`nonplan-route-${v.id}`}
                  >
                    <MapIcon size={12} /> View route
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {routeOpen && (
        <RouteModal data={routeOpen} onClose={() => setRouteOpen(null)} />
      )}
    </div>
  );
}

function RouteModal({ data, onClose }) {
  const { visit, gpsPath, loading } = data;
  const start = visit?.start;
  const end = visit?.end;
  const points = gpsPath || [];

  // Use Google Static Maps if API key is available AND admin hasn't
  // disabled the ``static_maps`` capability in the Google API panel.
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [staticEnabled, setStaticEnabled] = useState(true);
  useEffect(() => {
    let on = true;
    isGoogleEnabled('static_maps').then((v) => { if (on) setStaticEnabled(v); });
    return () => { on = false; };
  }, []);
  let staticUrl = null;
  if (apiKey && staticEnabled && points.length > 0) {
    const sample = points.length > 80 ? points.filter((_, i) => i % Math.ceil(points.length / 80) === 0) : points;
    const path = sample.map(p => `${p.latitude},${p.longitude}`).join('|');
    const center = `${start?.latitude},${start?.longitude}`;
    const markers = [
      `markers=color:green%7Clabel:S%7C${start?.latitude},${start?.longitude}`,
      end ? `markers=color:red%7Clabel:E%7C${end.latitude},${end.longitude}` : null,
    ].filter(Boolean).join('&');
    staticUrl = `https://maps.googleapis.com/maps/api/staticmap?size=620x360&center=${center}&zoom=13&path=color:0xff0000%7Cweight:4%7C${path}&${markers}&key=${apiKey}`;
  }
  // Each rendered static-map image = one Google call; track once per modal open.
  useEffect(() => {
    if (staticUrl) trackGoogle('static_maps', 'admin', true);
  }, [staticUrl]);

  const directionsUrl = end
    ? `https://www.google.com/maps/dir/${start?.latitude},${start?.longitude}/${end.latitude},${end.longitude}`
    : `https://maps.google.com/?q=${start?.latitude},${start?.longitude}`;

  return (
    <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4" data-testid="nonplan-route-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Route — {visit?.staff_name || visit?.staff_id}</h3>
            <p className="text-xs text-gray-500">
              {start?.time && new Date(start.time).toLocaleString('en-IN')} →
              {' '}{end?.time ? new Date(end.time).toLocaleString('en-IN') : 'still outside'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="route-modal-close">✕</button>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-gray-500"><Loader2 className="animate-spin" /></div>
        ) : (
          <>
            {staticUrl ? (
              <img src={staticUrl} alt="Route" className="w-full rounded-lg border" data-testid="route-static-map" />
            ) : (
              <div className="bg-gray-50 border rounded-lg p-6 text-center text-sm text-gray-500" data-testid="route-fallback">
                {points.length === 0
                  ? 'No GPS pings captured during this excursion.'
                  : `${points.length} GPS pings captured. Open the route in Google Maps:`}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-xs mt-3">
              <div className="bg-emerald-50 rounded p-2">
                <p className="text-gray-500">Started outside</p>
                <a className="text-emerald-700 font-medium hover:underline" target="_blank" rel="noreferrer"
                   href={`https://maps.google.com/?q=${start?.latitude},${start?.longitude}`}>
                  {start?.latitude?.toFixed(5)}, {start?.longitude?.toFixed(5)}
                </a>
              </div>
              <div className="bg-red-50 rounded p-2">
                <p className="text-gray-500">{end ? 'Returned at' : 'Last seen'}</p>
                {end ? (
                  <a className="text-red-700 font-medium hover:underline" target="_blank" rel="noreferrer"
                     href={`https://maps.google.com/?q=${end.latitude},${end.longitude}`}>
                    {end.latitude?.toFixed(5)}, {end.longitude?.toFixed(5)}
                  </a>
                ) : <span className="text-orange-700 font-medium">still outside</span>}
              </div>
              <div className="col-span-2">
                <a href={directionsUrl} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-blue-700 hover:underline">
                  <ExternalLink size={12} /> Open route in Google Maps
                </a>
                <span className="ml-3 text-gray-500">{points.length} pings · {visit?.duration_hours ? `${visit.duration_hours.toFixed(2)} hr` : (visit?.duration_minutes ? `${Math.round(visit.duration_minutes)} min` : '—')}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
